import type { StoreSpecial as V3StoreSpecial } from '@iheartjane/data-access';
import type { PriceId, ProductKind } from '@iheartjane/types';

import { isSupportedStoreSpecial } from '@jane/kiosk/util';
import type { AlgoliaPriceId } from '@jane/search/data-access';
import type { StoreSpecial } from '@jane/shared/types';
import type { WeightPriced } from '@jane/shared/util';
import {
  WEIGHT_FILTER_VALUES,
  availableWeightsForMenuProduct,
  getDiscountedUnitPrice,
  getUnitPrice,
  isDiscountSupported,
  lowestPriceForMenuProduct,
} from '@jane/shared/util';

export const useGetProductPrices = (
  product: WeightPriced & { kind: ProductKind },
  specials: (StoreSpecial | V3StoreSpecial)[],
  highlightDeal?: boolean,
  searchingByWeight?: AlgoliaPriceId[]
) => {
  return (priceId?: AlgoliaPriceId | PriceId) => {
    // AlgoliaPriceId is separated by spaces while PriceId uses _
    let finalPriceId = priceId?.split(' ').join('_') as PriceId;

    const supportedStoreSpecials =
      specials &&
      specials?.filter((special) => isSupportedStoreSpecial(special, true));

    if (searchingByWeight && searchingByWeight.length === 1) {
      finalPriceId = searchingByWeight[0].split(' ').join('_') as PriceId;
    }

    if (searchingByWeight && searchingByWeight.length > 1) {
      const sortedWeights = searchingByWeight.sort(
        (a, b) =>
          WEIGHT_FILTER_VALUES.indexOf(a) - WEIGHT_FILTER_VALUES.indexOf(b)
      );

      const filteredPriceId = sortedWeights.find((weight) =>
        getUnitPrice(product, weight.split(' ').join('_') as PriceId)
      );

      if (filteredPriceId) {
        finalPriceId = filteredPriceId.split(' ').join('_') as PriceId;
      }
    }

    if (!finalPriceId) {
      let weights = availableWeightsForMenuProduct(product);
      if (highlightDeal) {
        const discountedWeights = weights.filter(
          (weight) => !!getDiscountedUnitPrice(product, weight)
        );
        weights = discountedWeights.length > 0 ? discountedWeights : weights;
      }
      finalPriceId = lowestPriceForMenuProduct(product, weights) || 'each';
    }

    const discountInScope = isDiscountSupported(
      product,
      finalPriceId,
      supportedStoreSpecials
    );

    const discountPrice = discountInScope
      ? getDiscountedUnitPrice(product, finalPriceId)
      : undefined;

    const unitPrice = getUnitPrice(product, finalPriceId);

    return {
      discountPrice,
      price: discountPrice || unitPrice,
      priceId: finalPriceId,
      unitPrice,
    };
  };
};
